import { useCallback, useEffect, useRef, useState } from "react";

// Steps an anonymous visitor can actually use — the link only carries one of
// these; sharing from Role Play / Quiz (locked publicly) falls back to Reading.
export const PUBLIC_STEPS = new Set(["reading", "vocabulary", "listening"]);

// Public share link for a topic: /t/<token> renders the anonymous topic page,
// opening on the step the sharer was viewing (?step=…).
// Production links use the short muraja.app form (the landing vhost 301s it,
// query intact); anywhere else (dev, LAN) shares its own origin.
export function shareTopicUrl(token, step) {
  const base = window.location.hostname === "my.muraja.app"
    ? "https://muraja.app"
    : window.location.origin;
  const qs = PUBLIC_STEPS.has(step) && step !== "reading" ? `?step=${step}` : "";
  return `${base}/t/${token}${qs}`;
}

/**
 * useShareLink — the share-sheet / clipboard mechanics behind every "⤴ Share"
 * button (admin topic header, anonymous /t/<token> page).
 *
 * `share(resolveUrl)` awaits the URL (sync or async — the admin button mints
 * a token first, the public page already has it), then follows the Iʿrāb
 * share pattern: native share sheet only on touch devices, clipboard +
 * "Link copied ✓" on desktop, window.prompt as the fallback when the
 * clipboard is blocked. A thrown resolveUrl surfaces as `error`.
 *
 * Deliberately imports nothing from api.js so the public page can use it.
 */
export default function useShareLink() {
  const [shareState, setShareState] = useState("idle"); // idle | busy | copied
  const [error, setError] = useState(null);
  const shareResetRef = useRef(null);

  useEffect(() => () => clearTimeout(shareResetRef.current), []);

  const share = useCallback(async (resolveUrl) => {
    if (shareState === "busy") return;
    setError(null);
    setShareState("busy");
    let url;
    try {
      url = await resolveUrl();
    } catch (e) {
      setShareState("idle");
      setError(e.message || "Could not create the share link.");
      return;
    }
    const touchDevice = window.matchMedia?.("(pointer: coarse)")?.matches;
    if (navigator.share && touchDevice) {
      setShareState("idle");
      try {
        await navigator.share({ title: "مراجعة — Murājaʿa", url });
      } catch {
        // user dismissed the sheet — nothing else to do
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareState("copied");
      clearTimeout(shareResetRef.current);
      shareResetRef.current = setTimeout(() => setShareState("idle"), 2000);
    } catch {
      setShareState("idle");
      // Clipboard blocked (rare) — show the URL for manual copy.
      window.prompt("Copy this link:", url);
    }
  }, [shareState]);

  return { shareState, error, share };
}

// The button face shared by both share buttons: gold outline, gold-bright
// while "Link copied ✓" shows.
export function shareButtonStyle(shareState) {
  return shareState === "copied"
    ? { color: "var(--gold-bright, #c9a227)", borderColor: "var(--gold, #c9a227)", background: "transparent" }
    : { color: "var(--gold, #8a6d1d)", borderColor: "var(--gold, #c9a227)", background: "transparent" };
}

export function shareButtonLabel(shareState) {
  return shareState === "copied" ? "Link copied ✓" : shareState === "busy" ? "Sharing…" : "⤴ Share";
}
